import {simpleCurrency} from 'app/format/currency'
import {filter, get, set, startCase} from 'lodash'
import {Order, RefundOrder, RefundOrderState} from '../../models/OrderRefundState'
import {mapToVariantAllowNegativeInventoryModel, sellingPrice} from '../../helper'
import sumBy from 'lodash/sumBy'
import omit from 'lodash/omit'
import round from 'lodash/round'
import map from 'lodash/map'
import capitalize from 'lodash/capitalize'
import _ from 'lodash'
import {orderFulfillmentStatusTypes, typesPayment} from '../../models'
import {ViewOrder} from '../../models/OrderState'
import {findFreeShippingDiscount} from '../draft/helper'
import {PAYMENT_TYPES, transactionPaymentTypes} from 'app/constant/payment'
import {orderTypes} from 'app/constant'
import groupBy from 'lodash/groupBy'
import lowerCase from 'lodash/lowerCase'

export const actionType = {
  add: 'ADD',
  remove: 'REMOVE',
  update: 'UPDATE',
}

export const provider = {
  stripe: 'stripe',
  elavon: 'elavon',
  payEngine: 'pay_engine',
}

export const addressTypes = {
  shipping: 'SHIPPING',
  billing: 'BILLING',
}

export const mapToViewOrderModel = (data: any) => {
  const {
    customer,
    shippingAddress,
    orderAddressProfiles,
    transactions,
    paymentMethod,
    billingAddress,
  } = data
  const paymentType = data.paymentType
    ? getPaymentType(data.paymentType)
    : mapToPaymentType(transactions?.nodes)
  const order: ViewOrder = {
    ...data,
    email: data.email ?? customer?.email,
    phone: data.phone ?? customer?.phone,
    subtotalPrice: parseFloat(data.subtotalPrice),
    totalPrice: parseFloat(data.totalPrice),
    totalTax: parseFloat(data.totalTax),
    totalDiscounts: parseFloat(data.totalDiscounts),
    shippingPrice: parseFloat(data.shippingPrice),
    orderDeliveryMethod: data.orderDeliveryMethod,
    wineClubDiscounts: parseFloat(data.wineClubDiscounts),
    note: data.note || '',
    customer: {
      ...customer,
      shippingAddress: mapToAddress(
        orderAddressProfiles?.nodes,
        shippingAddress,
        addressTypes.shipping
      ),
      billingAddress: mapToAddress(
        orderAddressProfiles?.nodes,
        billingAddress,
        addressTypes.billing
      ),
      paymentMethod,
      transactions: transactions?.nodes,
      email: data.email ?? customer?.email,
      customerName: data.name,
      phone: data.phone ?? customer?.phone,
    },
    fulfillments: data.fulfillments?.nodes,
    orderItems: data.orderItems?.nodes,
    orderHistory: mapToHistoryModel(data.orderHistories?.nodes),
    refundItems: data.refunds?.nodes,
    notes: data.notes ?? [],
    salesTax: data.salesTax ? parseFloat(data.salesTax) : 0,
    crv: data.crv ? parseFloat(data.crv) : 0,
    isCrvEnabled: data.isCrvEnabled,
    paymentType,
    cardInfo:
      data.orderType === orderTypes.POS ||
      data.paymentType === transactionPaymentTypes.googlePay ||
      data.paymentType === transactionPaymentTypes.applePay
        ? mapToCardInfo(transactions?.nodes, data?.paymentProviderType)
        : null,
    migratedOrderItems: mapToMigratedItemsModel(data.migratedOrderItems?.nodes),
    orderTrackings: mapToOrderTrackingModel(data.externalOrderTrackings?.nodes),
    paymentProviderType: data?.paymentProviderType,
    giftMessage: data.giftMessage,
    futureShpDate: data?.futureShpDate,
    terminalName: mapToTerminalNameModel(data?.transactions?.nodes),
    isPaidByGiftCard: mapToPaidByGiftCard(transactions?.nodes),
    isIncludedScannerTransaction: checkIsTransactionIsExist(
      transactions?.nodes,
      typesPayment.scanner
    ),
    totalAmountPaid: mapToModelTotalAmountPaid(transactions?.nodes),
    paymentMethodId: data.paymentMethod?.id || null,
    paymentToken: data.paymentToken || null,
  }

  return order
}

const mapToPaidByGiftCard = (transactions: any) => {
  if (transactions && transactions.length > 0) {
    return transactions
      .map((transaction: any) => transaction.paymentType)
      .includes(typesPayment.giftCard)
  }
  return false
}

const checkIsTransactionIsExist = (transactions: any, transactionType: string) => {
  if (transactions && transactions.length > 0) {
    return transactions.map((transaction: any) => transaction.paymentType).includes(transactionType)
  }
  return false
}

const mapToPaymentType = (transactions: any[]) => {
  if (transactions && transactions.length === 1) {
    const transaction = transactions[0]
    return getPaymentType(transaction.paymentType)
  }
  return null
}

const getPaymentType = (paymentType: string) => {
  if (paymentType === transactionPaymentTypes.scanner) {
    return PAYMENT_TYPES.scanner
  } else if (paymentType === transactionPaymentTypes.creditCard) {
    return PAYMENT_TYPES.creditCard
  }
  return startCase(lowerCase(paymentType))
}

const mapToCardInfo = (transactions: any[], paymentProvider: string) => {
  if (!transactions?.length) return null

  try {
    const transaction = transactions[0]
    const paymentDetails = transaction?.metaData

    if (
      (transaction.paymentType === transactionPaymentTypes.googlePay ||
        transaction.paymentType === transactionPaymentTypes.applePay) &&
      paymentProvider === provider.payEngine
    ) {
      return mapToPayEngineScannerInfo(paymentDetails)
    }

    switch (transaction.paymentType) {
      case transactionPaymentTypes.scanner:
        switch (paymentProvider) {
          case provider.elavon:
            return mapToElavonCardInfo(paymentDetails)
          case provider.payEngine:
            return mapToPayEngineScannerInfo(paymentDetails)
          default:
            return mapToStipeCardInfo(paymentDetails)
        }

      case transactionPaymentTypes.creditCard:
        if (paymentProvider === provider.payEngine) {
          return mapToPayEngineCardInfo(paymentDetails)
        }
        return null

      default:
        return null
    }
  } catch (error) {
    console.error('Error mapping card info:', error)
    return null
  }
}

const mapToStipeCardInfo = (metaData: any) => {
  if (metaData && metaData.charges) {
    const details = metaData.charges?.data
    if (details && details.length) {
      const detail = details[0]
      const accountInfo = detail?.payment_method_details?.card_present
      return {
        last4: accountInfo?.last4 ?? null,
        brand: accountInfo?.brand ?? null,
      }
    }
  }
}

const mapToElavonCardInfo = (metaData: any) => {
  if (metaData && metaData.response) {
    const {accountInformation, additionalInformation} = metaData.response
    return {
      last4:
        accountInformation?.account && accountInformation.account?.length > 3
          ? accountInformation.account
          : null,
      brand: additionalInformation?.APPLAB ? _.capitalize(additionalInformation.APPLAB) : null,
    }
  }
}

const mapToPayEngineScannerInfo = (metaData: any) => {
  if (metaData && metaData.response) {
    const {data} = JSON.parse(metaData.response)
    if (data?.SaleResponse) {
      const maskedCardNumber = data.SaleResponse?.maskedCardNumber
      return {
        last4: maskedCardNumber ? maskedCardNumber.slice(-4) : null,
        brand: data.SaleResponse?.cardType ?? null,
      }
    }
    return null
  }
  return null
}
const mapToPayEngineCardInfo = (metaData: any) => {
  if (metaData && metaData.metaData) {
    const {data} = JSON.parse(metaData.metaData)
    if (data?.CaptureResponse) {
      const maskedCardNumber = data.CaptureResponse?.maskedCardNumber
      return {
        last4: maskedCardNumber ? maskedCardNumber.slice(-4) : null,
        brand: data.CaptureResponse?.cardType ?? null,
      }
    }
    return null
  }
  return null
}
const mapToHistoryModel = (history: any) => {
  if (history) {
    return history.map(({refundItems, orderItems, ...his}: any) => {
      const newItems = filter(orderItems.nodes, (orderItem) => !orderItem.orderItemId)
      const updatedItems = filter(orderItems.nodes, (orderItem) => !!orderItem.orderItemId)
      const user = `${capitalize(his.user?.firstName)} ${capitalize(his.user?.lastName)}`
      return {
        ...his,
        refundItems: mapToViewOrderItemsModel(refundItems.nodes),
        newItems: mapToViewOrderItemsModel(newItems),
        updatedItems: mapToViewOrderItemsModel(updatedItems),
        user,
      }
    })
  }
  return []
}

export const mapToViewOrderItemsModel = (orderItems: any[], name?: string) => {
  return filter(orderItems, (item: any) => {
    let qty = item.quantity
    if (name) {
      qty = get(item, name)
    }
    return qty > 0
  }).map((item: any) => {
    let qty = item.quantity
    let product, id, available
    if (item.variant) {
      product = item.variant.product
      available = item.variant.available
      id = item.variant.id
    }
    if (name) {
      qty = get(item, name)
    }
    return {
      ...item,
      product,
      quantity: qty,
      variantId: id,
      totalPrice: qty * sellingPrice(item),
      available,
      originalQty: qty,
      orderItemId: item.id,
      price: sellingPrice(item),
      originalPrice: item.variant.price,
    }
  })
}

export const mapToFulfilledItemsModel = (orderItems: any[]) => {
  return orderItems.map(({quantity, refundedQuantity, ...item}: any) => {
    refundedQuantity = parseInt(refundedQuantity)
    if (refundedQuantity > 0) {
      quantity -= refundedQuantity
    }
    const {variant} = item
    const price: any = parseFloat(item.price)
    let product, id
    if (variant) {
      product = variant.product
      id = variant.id
    }
    return {
      ...item,
      quantity,
      product,
      variantId: id,
      price,
      totalPrice: parseInt(quantity) * price,
      originalPrice: item.variant.price,
    }
  })
}

export const checkOrderItemIsAlreadyExist = (newItems: any[], deletedItems: any[]) => {
  return deletedItems.filter((delObj) => {
    return newItems.find((newObj) => {
      return delObj.variantId === newObj.variantId
    })
  })
}

export const mapDeleteOrderItemModel = (newItems: any[], deletedItems: any[]) => {
  const items = deletedItems.filter((delObj) => {
    return !newItems.find((newObj) => {
      return delObj.variantId === newObj.variantId
    })
  })
  return map(items, 'id')
}

export const mapToEditOrderItemModel = (orderItems: any[]) => {
  const items = mapToVariantAllowNegativeInventoryModel(orderItems)
  return items.map((item: any) => {
    const {variant} = item
    const {product} = variant
    // const available =
    //   item.variant.available < 0 ? parseInt(item.quantity) : parseInt(item.variant.available)
    const available = item.available < 0 ? parseInt(item.quantity) : parseInt(item.available)
    return {
      id: item.id,
      productId: product.id,
      variantId: item.variantId,
      quantity: parseInt(item.quantity),
      price: parseFloat(item.price),
      totalPrice: parseFloat(item.price) * parseInt(item.quantity),
      variant: variant.name,
      product,
      originalQty: parseInt(item.quantity),
      available,
      orderEditId: item.orderEditId,
      weight: variant.weight,
      orderItemId: item.orderItemId,
      shipAvailableForAdminPortal: available,
      pickupAvailableForAdminPortal: available,
      originalPrice: item.variant.price,
    }
  })
}

export const mapToRefundOrderItemModel = (orderItems: any[]) => {
  return orderItems.map((item: any) => {
    const {variant, price} = item
    const {product} = variant
    const available = parseInt(item.quantity)
    const salesTax = parseFloat(item.salesTax)
    const crv = parseFloat(item.crv ?? 0)
    return {
      id: item.id,
      productId: product.id,
      variantId: item.variantId,
      quantity: parseInt(item.quantity),
      originalQty: parseInt(item.quantity),
      price: parseFloat(price),
      totalPrice: parseFloat(price) * parseInt(item.quantity),
      variant: variant.name,
      product,
      available,
      orderEditId: item.orderEditId,
      weight: variant.weight,
      orderItemId: item.orderItemId,
      salesTax,
      crv,
      shipAvailableForAdminPortal: available,
      pickupAvailableForAdminPortal: available,
      itemOriginalQty: parseInt(item.originalQuantity),
      originalSaleTax: salesTax,
      originalCrv: crv,
    }
  })
}
export const mapToOrderEditItemsModel = (orderItems: any[]) => {
  return orderItems.map((item) => {
    return {
      quantity: parseInt(item.quantity),
      orderEditId: item.orderEditId,
      price: sellingPrice(item),
      id: item.id,
      variantId: item.variantId,
      orderItemId: item.orderItemId,
    }
  })
}

export const mapToRefundOrderItemsModel = (orderItems: any[]) => {
  return orderItems.map((item) => {
    return {
      quantity: parseInt(item.quantity),
      orderEditId: item.orderEditId,
      price: sellingPrice(item),
      id: item.id,
      variantId: item.variantId,
      orderItemId: item.orderItemId,
      salesTax: parseFloat(item.salesTax),
      crv: parseFloat(item.crv),
    }
  })
}

export const calculateSubTotalPrice = (items: any[]) => {
  return sumBy(items, (item: any) => {
    const price = sellingPrice(item)
    if (item.quantity) {
      return item.quantity * price
    }
    return 0
  })
}

export const getItemValue = (itemTax: any, qty: any, originalQty: any) => {
  if (itemTax > 0) {
    const taxValue = parseFloat(itemTax) / parseInt(originalQty)
    return round(taxValue, 5) * qty
  }
  return 0
}

export const calculateSalesTax = (items: any[]) => {
  return sumBy(items, (item: any) => {
    return getItemValue(
      item.originalSaleTax - item.originalCrv,
      item.quantity,
      item.itemOriginalQty
    )
  })
}

export const calculateCrvTax = (items: any[]) => {
  return sumBy(items, (item: any) => {
    return getItemValue(item.originalCrv, item.quantity, item.itemOriginalQty)
  })
}

export const mapToUpdateOrderEdit = ({
  orderEditId,
  shippingAddress,
  billingAddress,
  paymentMethod,
  id,
  shippingRate,
  __typename,
  isCompliant,
  totalTax,
  shippingRateId,
  isFreeShipping,
  orderItems,
  prevOrderItems,
  refundPrice,
  maxRefundPrice,
  selectedItemDiscounts,
  ...props
}: Order | any) => {
  return {
    ...omit(props, [
      'totalDiscounts',
      'wineClubDiscounts',
      'discountCodeId',
      'discounts',
      '__typename',
      'shippingDiscountCodeId',
      'wineClubDiscountCodeId',
      'isCustomRate',
      'isWineMackerChoiceDiscountDisabled',
      'orderAddressProfiles',
      'phone',
      'email',
      'tipAmount',
    ]),
    shippingRateId,
    totalTax: parseFloat(totalTax),
    id: orderEditId,
    note: props.note?.length > 0 ? props.note : null,
  }
}

export const mapToRefundUpdateModel = ({
  refund,
  viewOrder,
  deletedItems,
  isRestock,
  restockLocationId,
  ...rest
}: RefundOrderState): any => {
  const order: ViewOrder = {...viewOrder}
  const newState: any = {}
  const removeDiscountIds = refund.removeDiscountIds || []
  const discounts = filter(refund.discounts, ({amount}) => amount > 0).map((discount) => {
    return {
      id: discount.id,
      discountCodeId: discount.discountCodeId,
      amount: parseFloat(discount.amount),
      isWineClub: discount.isWineClub,
    }
  })
  newState.id = refund.orderEditId
  newState.note = refund.note?.length > 0 ? refund.note : null
  newState.shippingPrice = parseFloat(order.shippingPrice)
  newState.totalTax = parseFloat(order.totalTax)
  newState.salesTax = parseFloat(order.salesTax)
  newState.crv = parseFloat(order.crv)
  newState.totalPrice = parseFloat(order.totalPrice)
  newState.wineClubDiscounts = parseFloat(order.wineClubDiscounts)
  newState.totalDiscounts = parseFloat(order.totalDiscounts)
  newState.subtotalPrice = parseFloat(order.subtotalPrice) - calculateSubTotalPrice(deletedItems)

  if (refund.wineClubDiscounts > 0) {
    newState.wineClubDiscounts -= parseFloat(refund.wineClubDiscounts)
  }

  if (refund.totalDiscounts > 0) {
    newState.totalDiscounts -= parseFloat(refund.totalDiscounts)
  }

  if (parseFloat(refund.totalDiscounts) === 0) {
    newState.discountCodeId = null
  }

  if (parseFloat(refund.wineClubDiscounts) === 0) {
    newState.wineClubDiscountCodeId = null
  }

  if (refund.shippingRefundPrice > 0) {
    newState.shippingPrice -= parseFloat(refund.shippingRefundPrice)
    newState.shippingRefundAmount = parseFloat(refund.shippingRefundPrice)
  }

  if (refund.totalTax > 0) {
    newState.totalTax -= parseFloat(refund.totalTax)
  }

  if (refund.salesTax > 0) {
    newState.salesTax -= parseFloat(refund.salesTax)
  }

  if (refund.shippingTax > 0) {
    newState.salesTax -= parseFloat(refund.shippingTax)
  }

  if (refund.crv > 0) {
    newState.crv -= parseFloat(refund.crv)
  }

  if (refund.orderRefundPrice > 0) {
    newState.customRefundAmount = parseFloat(refund.orderRefundPrice)
    newState.totalPrice -= parseFloat(refund.orderRefundPrice)
  } else {
    newState.totalPrice -= parseFloat(refund.totalPrice)
  }

  if (newState.totalTax <= 0) {
    newState.totalTax = 0
  }

  if (isRestock) {
    newState.restockLocationId = restockLocationId
  }

  newState.freightSalesTax = parseFloat(refund.freightSalesTax ?? 0)

  newState.totalPrice = round(newState.totalPrice, 2)

  const orderEdit = {
    ...omit(newState, [
      'totalDiscounts',
      'wineClubDiscounts',
      'discountCodeId',
      'discounts',
      '__typename',
      'shippingDiscountCodeId',
      'wineClubDiscountCodeId',
      'shippingTax',
      'orderAddressProfiles',
      'phone',
      'email',
      'tipAmount',
    ]),
  }
  return {removeDiscountIds, discounts, orderEdit}
}

export const mapToCustomRefundUpdateModel = ({refund, viewOrder}: RefundOrderState): any => {
  const order: ViewOrder = {...viewOrder}
  const newState: any = {}
  newState.id = refund.orderEditId
  newState.totalTax = parseFloat(order.totalTax)
  newState.salesTax = parseFloat(order.salesTax)
  newState.crv = parseFloat(order.crv)
  newState.note = refund.note?.length > 0 ? refund.note : null
  newState.shippingPrice = parseFloat(order.shippingPrice)
  newState.totalPrice = parseFloat(order.totalPrice)
  newState.subtotalPrice = parseFloat(order.subtotalPrice)
  newState.customRefundAmount = parseFloat(refund.orderRefundPrice)
  newState.totalPrice -= parseFloat(refund.orderRefundPrice)
  newState.freightSalesTax = parseFloat(order.freightSalesTax)
  return newState
}

const calculateProductTotalWeight = (items: any[]) => {
  const totalWeight = sumBy(items, (item: any) => {
    if (item.quantity) {
      return item.quantity * item.weight.value
    }
    return 0
  })
  return totalWeight
}

const calculateProductTotalPrice = (items: any[]) => {
  const totalPrice = sumBy(items, (item: any) => {
    if (item.quantity) {
      return item.quantity * sellingPrice(item)
    }
    return 0
  })
  return totalPrice
}

export const mapShippingOrderModel = (address: any, items: any[]) => {
  const productTotalPrice: any = calculateProductTotalPrice(items)
  const productTotalWeight: any = calculateProductTotalWeight(items)
  return {
    shippingAddress: {
      countryCode: address.country.code || 'us',
      postalCode: address.zipCode,
      stateOrProvinceCode: address.state.code,
    },
    weight: {
      value: parseFloat(productTotalWeight),
      units: 'LB',
    },
    totalPrice: parseFloat(productTotalPrice),
  }
}

export const calculateTotalPrice = (order: Order | any) => {
  let totalPrice = parseFloat(order.subtotalPrice)

  if (order.totalTax) {
    totalPrice += parseFloat(order.totalTax)
  }

  if (order.totalDiscounts) {
    totalPrice -= parseFloat(order.totalDiscounts)
  }

  if (order.wineClubDiscounts) {
    totalPrice -= parseFloat(order.wineClubDiscounts)
  }

  if (order.shippingPrice) {
    totalPrice += parseFloat(order.shippingPrice)
  }

  if (order.tipAmount) {
    totalPrice += parseFloat(order.tipAmount)
  }
  return totalPrice
}

export const calculateFulfillmentOrderTotal = (viewOrder: ViewOrder) => {
  if (viewOrder.fulfillmentStatus === orderFulfillmentStatusTypes.partial) {
    const items: any[] = []
    const fulfillment: any = _.map(viewOrder.fulfillments)
    _.forIn(fulfillment, function (value: any) {
      items.push(value.fulfillmentItems.nodes)
    })
    const fulfilledItems: any = _.flattenDeep(items).filter(function () {
      return true
    })
    const fulfilledItemsTotal: any = calculateSubTotalPrice(fulfilledItems)
    return fulfilledItemsTotal
  }
  return 0
}

export const mapToOrderEditDiscountModel = (discounts: any[]) => {
  let isPromoCode = false,
    isWineClub = false,
    isShipping = false,
    totalDiscounts = 0,
    wineClubDiscounts = 0,
    discountCodeId = null,
    shippingDiscountCodeId = null,
    wineClubDiscountCodeId = null,
    code = '',
    name = '',
    shipDiscountCode = '',
    isFreeShipping = false,
    shipDiscountName = '',
    promoCodeId = '',
    wineClubCodeId = ''

  discounts.forEach((discount: any) => {
    if (discount.isPromoCode) {
      isPromoCode = true
      totalDiscounts = parseFloat(discount.amount)
      discountCodeId = discount.discountCodeId
      code = discount.code
      promoCodeId = discount.id
    }

    if (discount.isShipping) {
      shipDiscountCode = discount.code
      isFreeShipping = findFreeShippingDiscount(discount)
      if (isFreeShipping) {
        shipDiscountCode = discount.code
      }
      isShipping = true
      shipDiscountName = discount.name
      shippingDiscountCodeId = discount.discountCodeId
    }

    if (discount.isWineClub) {
      isWineClub = true
      wineClubDiscounts = parseFloat(discount.amount)
      if (discount.isDiscountOverride) {
        name = discount.customDiscountPercentage
          ? `${discount.customDiscountPercentage} % Discount`
          : `${simpleCurrency(discount.amount)} Discount`
      } else {
        name = discount.name
      }

      wineClubDiscountCodeId = discount.discountCodeId
      wineClubCodeId = discount.id
    }
    if (!discount.discountCodeId) {
      promoCodeId = discount.id
      totalDiscounts = parseFloat(discount.amount)
      code = discount.customDiscountPercentage
        ? `${discount.customDiscountPercentage} % Discount`
        : `${simpleCurrency(discount.amount)} Discount`
    }
  })

  return {
    isPromoCode,
    isWineClub,
    isShipping,
    totalDiscounts,
    wineClubDiscounts,
    discountCodeId,
    code,
    name,
    shippingDiscountCodeId,
    shipDiscountCode,
    isFreeShipping,
    shipDiscountName,
    wineClubDiscountCodeId,
    promoCodeId,
    wineClubCodeId,
  }
}

const mapToMigratedItemsModel = (items: any[]) => {
  if (items && items.length > 0) {
    return items.map((item: any) => {
      return {
        totalPrice: parseFloat(item.amount),
        quantity: parseInt(item.quantity),
        price: parseFloat(item.amount) / parseInt(item.quantity),
        product: {
          name: item.sku,
          variant: null,
        },
      }
    })
  }
  return []
}

const mapToOrderTrackingModel = (trackings: any) => {
  if (trackings && trackings.length > 0) {
    const completedList = filter(trackings, (tracking: any) => tracking.status === 'CREATED')
    if (completedList && completedList.length > 0) {
      const completeGroup = groupBy(completedList, 'appType')
      const keys = Object.keys(completeGroup)
      return filter(trackings, ({appType}) => !keys.includes(appType))
    }
    return trackings
  }
  return []
}

const mapToTerminalNameModel = (transactions: any) => {
  if (transactions && transactions.length > 0) {
    const foundTransaction = transactions.find(
      (value: any) => value.paymentType === transactionPaymentTypes.scanner
    )
    return foundTransaction ? foundTransaction.terminalName : null
  } else {
    return null
  }
}

const mapToModelTotalAmountPaid = (transactions: any[]): number => {
  return sumBy(transactions, (item) => {
    if (item.status === 'SUCCESS') {
      return parseFloat(item.amount) || 0
    }
    return 0
  })
}

export const mapToAddress = (orderAddress: any, address: any, type: string) => {
  if (orderAddress && orderAddress.length > 0) {
    const mappedAddress = orderAddress.find(
      (addr: any) => lowerCase(addr.addressType) === lowerCase(type)
    )
    return mappedAddress || address
  }
  return address
}
